import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  Settings,
  Server,
  Globe,
  ArrowRight,
  ArrowLeft,
  FolderDown,
  FolderUp,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Database,
  Cable,
  Copy,
  Play,
  Download,
  CheckCircle,
  TestTube,
  File,
  Save,
} from "lucide-react";

const AdvancedToolsCard = React.memo(function AdvancedToolsCard({
  handleOpenEnvVarsDialog,
  migrationDialogOpen,
  setMigrationDialogOpen,
  migrationMode,
  setMigrationMode,
  migrationStep,
  setMigrationStep,
  exportingMigration,
  setExportingMigration,
  newDbConnectionString,
  setNewDbConnectionString,
  newDbName,
  setNewDbName,
  testingConnection,
  setTestingConnection,
  connectionTestResult,
  setConnectionTestResult,
  migrationFile,
  setMigrationFile,
  migratingData,
  setMigratingData,
  migrationResult,
  setMigrationResult,
  migrationFileInputRef,
  resetMigrationDialog,
  handleExportMigration,
  handleTestConnection,
  handleImportMigration,
  importingFromMP,
  setImportingFromMP,
  importFromMPResult,
  setImportFromMPResult,
  handleImportFromMP,
  envVarsDialogOpen,
  setEnvVarsDialogOpen,
  envVars,
  editedEnvVars,
  loadingEnvVars,
  savingEnvVars,
  activeEnvTab,
  setActiveEnvTab,
  handleEnvVarChange,
  handleSaveEnvVars,
  hasEnvChanges,
}) {
  return (
    <>
      {/* Environment Variables Card */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-emerald-900 flex items-center gap-2">
            <Cable className="h-5 w-5" />
            Environment Variables
          </CardTitle>
          <CardDescription className="text-emerald-600">
            View and edit environment variables for backend and frontend configurations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              <p>Manage API keys, URLs, and other configuration settings stored in .env files.</p>
              <p className="text-amber-600 mt-1 text-xs">⚠️ Changes require service restart to take effect</p>
            </div>
            <Button
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-100"
              onClick={handleOpenEnvVarsDialog}
            >
              <Settings className="mr-2 h-4 w-4" />
              Manage Variables
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Database Migration Card */}
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-purple-900 flex items-center gap-2">
            <Server className="h-5 w-5" />
            Database Migration
          </CardTitle>
          <CardDescription className="text-purple-600">
            Export complete database with schema & indexes, or migrate to a new MongoDB server
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Export Migration Package */}
            <div className="border border-purple-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FolderDown className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-900">Export Migration Package</h4>
                  <p className="text-xs text-purple-600">Schema + Indexes + All Data</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Download a complete migration package containing all collections, indexes, and data.
                Use this to set up the app on a new server.
              </p>
              <Button
                onClick={() => {
                  setMigrationMode('export');
                  setMigrationStep(2);
                  setMigrationDialogOpen(true);
                }}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="export-migration-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Migration Package
              </Button>
            </div>

            {/* Import to New Database */}
            <div className="border border-purple-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Globe className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-900">Create Database on New Server</h4>
                  <p className="text-xs text-purple-600">Replicate to any MongoDB instance</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Connect to a new MongoDB server and import your migration package.
                Creates all collections, indexes, and imports all data.
              </p>
              <Button
                onClick={() => {
                  resetMigrationDialog();
                  setMigrationMode('import');
                  setMigrationStep(2);
                  setMigrationDialogOpen(true);
                }}
                variant="outline"
                className="w-full border-purple-500 text-purple-700 hover:bg-purple-50"
                data-testid="import-migration-btn"
              >
                <Cable className="mr-2 h-4 w-4" />
                Create Database on New Server
              </Button>
            </div>

            {/* Import from Migration Package */}
            <div className="border border-red-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FolderUp className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h4 className="font-medium text-red-900">Import from MP</h4>
                  <p className="text-xs text-red-600">Overwrite current database</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Select a previously exported migration package to restore. This will overwrite all existing data.
              </p>
              <Button
                onClick={() => {
                  resetMigrationDialog();
                  setMigrationMode('import-local');
                  setMigrationStep(2);
                  setMigrationDialogOpen(true);
                }}
                variant="outline"
                className="w-full border-red-500 text-red-700 hover:bg-red-50"
                data-testid="import-from-mp-btn"
              >
                <FolderUp className="mr-2 h-4 w-4" />
                Import from MP
              </Button>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-800">
              <strong>Use Case:</strong> Migrate your CRM from Emergent's hosted MongoDB to your own cloud server
              (AWS, Google Cloud, DigitalOcean, etc.) or MongoDB Atlas.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Database Migration Dialog */}
      <Dialog open={migrationDialogOpen} onOpenChange={(open) => {
        setMigrationDialogOpen(open);
        if (!open) resetMigrationDialog();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              {migrationMode === 'export' ? (
                <>
                  <FolderDown className="h-5 w-5" />
                  Export Migration Package
                </>
              ) : migrationMode === 'import-local' ? (
                <>
                  <FolderUp className="h-5 w-5 text-red-600" />
                  <span className="text-red-900">Import from Migration Package</span>
                </>
              ) : (
                <>
                  <Globe className="h-5 w-5" />
                  Create Database on New Server
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {migrationMode === 'export'
                ? 'Export complete database with schema, indexes, and all data as JSON.'
                : migrationMode === 'import-local'
                ? 'Import a migration package into the current database. This will overwrite all existing data.'
                : 'Connect to a new MongoDB server and replicate your database.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Export Mode */}
            {migrationMode === 'export' && (
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">Migration Package Contents:</h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      All collection schemas and index definitions
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Complete data from all collections
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Users, Leads, Companies, Franchise data, Documents, etc.
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Migration instructions included in file
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={handleExportMigration}
                  disabled={exportingMigration}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {exportingMigration ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {exportingMigration ? 'Exporting...' : 'Download Migration Package'}
                </Button>
              </div>
            )}

            {/* Import from MP Mode (current database) */}
            {migrationMode === 'import-local' && (
              <div className="space-y-6">
                {/* Step 1: Upload File */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-red-600 text-white text-sm flex items-center justify-center">1</span>
                    Select Migration Package File
                  </h4>
                  <div className="border-2 border-dashed border-red-300 rounded-lg p-4 text-center">
                    <input
                      ref={migrationFileInputRef}
                      type="file"
                      accept=".json"
                      onChange={(e) => {
                        setMigrationFile(e.target.files[0]);
                        setImportFromMPResult(null);
                      }}
                      className="hidden"
                      id="import-mp-file-input"
                    />
                    <label htmlFor="import-mp-file-input" className="cursor-pointer">
                      <FolderUp className="h-10 w-10 text-red-400 mx-auto mb-2" />
                      <p className="text-sm text-red-600">
                        {migrationFile ? (
                          <span className="flex items-center justify-center gap-2">
                            <File className="h-4 w-4" />
                            {migrationFile.name}
                          </span>
                        ) : 'Click to select migration package (.json)'}
                      </p>
                    </label>
                  </div>
                </div>

                {/* Step 2: Confirm & Import */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-red-600 text-white text-sm flex items-center justify-center">2</span>
                    Confirm Import
                  </h4>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Warning:</strong> This will DROP and replace all collections in the <strong>current database</strong> with data from the migration package. This action cannot be undone. Make sure you have a backup before proceeding.
                      </span>
                    </p>
                  </div>

                  <Button
                    onClick={handleImportFromMP}
                    disabled={importingFromMP || !migrationFile}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    {importingFromMP ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="mr-2 h-4 w-4" />
                    )}
                    {importingFromMP ? 'Importing Data...' : 'Import & Overwrite Current Database'}
                  </Button>

                  {importFromMPResult && (
                    <div className={`p-4 rounded-lg ${importFromMPResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {importFromMPResult.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        )}
                        <span className={`font-medium ${importFromMPResult.success ? 'text-green-700' : 'text-red-700'}`}>
                          {importFromMPResult.success ? 'Import Successful!' : 'Import Failed'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{importFromMPResult.message}</p>

                      {importFromMPResult.results && (
                        <div className="mt-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded p-2">
                              <p className="text-slate-500">Collections Updated</p>
                              <p className="font-medium text-slate-800">{importFromMPResult.results.collections_updated?.length || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-slate-500">Total Documents</p>
                              <p className="font-medium text-slate-800">
                                {Object.values(importFromMPResult.results.data_imported || {}).reduce((a, b) => a + b, 0)}
                              </p>
                            </div>
                          </div>
                          {importFromMPResult.results.errors?.length > 0 && (
                            <div className="mt-2 p-2 bg-red-50 rounded">
                              <p className="text-red-700 font-medium">Errors:</p>
                              <ul className="text-red-600 text-xs">
                                {importFromMPResult.results.errors.map((err, idx) => (
                                  <li key={idx}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Import Mode (to new server) */}
            {migrationMode === 'import' && (
              <div className="space-y-6">
                {/* Step 1: Upload Migration File */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center">1</span>
                    Select Migration Package File
                  </h4>
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center">
                    <input
                      ref={migrationFileInputRef}
                      type="file"
                      accept=".json"
                      onChange={(e) => {
                        setMigrationFile(e.target.files[0]);
                        setMigrationResult(null);
                      }}
                      className="hidden"
                      id="migration-file-input"
                    />
                    <label htmlFor="migration-file-input" className="cursor-pointer">
                      <FolderUp className="h-10 w-10 text-purple-400 mx-auto mb-2" />
                      <p className="text-sm text-purple-600">
                        {migrationFile ? (
                          <span className="flex items-center justify-center gap-2">
                            <File className="h-4 w-4" />
                            {migrationFile.name}
                          </span>
                        ) : 'Click to select migration package (.json)'}
                      </p>
                    </label>
                  </div>
                </div>

                {/* Step 2: Enter Connection Details */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center">2</span>
                    New MongoDB Connection Details
                  </h4>
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Connection String</Label>
                      <Input
                        type="password"
                        placeholder="mongodb+srv://user:password@cluster.mongodb.net/"
                        value={newDbConnectionString}
                        onChange={(e) => {
                          setNewDbConnectionString(e.target.value);
                          setConnectionTestResult(null);
                        }}
                        className="mt-1 font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Get this from MongoDB Atlas or your cloud provider
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Database Name</Label>
                      <Input
                        type="text"
                        placeholder="dailylog_production"
                        value={newDbName}
                        onChange={(e) => {
                          setNewDbName(e.target.value);
                          setConnectionTestResult(null);
                        }}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      onClick={handleTestConnection}
                      disabled={testingConnection || !newDbConnectionString || !newDbName}
                      variant="outline"
                      className="w-full"
                    >
                      {testingConnection ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="mr-2 h-4 w-4" />
                      )}
                      {testingConnection ? 'Testing...' : 'Test Connection'}
                    </Button>

                    {connectionTestResult && (
                      <div className={`p-3 rounded-lg ${connectionTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2">
                          {connectionTestResult.success ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                          )}
                          <span className={`font-medium ${connectionTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                            {connectionTestResult.success ? 'Connection Successful!' : 'Connection Failed'}
                          </span>
                        </div>
                        {connectionTestResult.success && connectionTestResult.server_info && (
                          <p className="text-sm text-green-600 mt-1">
                            MongoDB v{connectionTestResult.server_info.version}
                          </p>
                        )}
                        {connectionTestResult.warning && (
                          <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            {connectionTestResult.warning}
                          </p>
                        )}
                        {!connectionTestResult.success && (
                          <p className="text-sm text-red-600 mt-1">{connectionTestResult.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3: Migrate */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center">3</span>
                    Run Migration
                  </h4>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800 flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Warning:</strong> This will DROP all existing collections in the target database
                        and replace them with data from the migration package. This action cannot be undone.
                      </span>
                    </p>
                  </div>

                  <Button
                    onClick={handleImportMigration}
                    disabled={migratingData || !migrationFile || !connectionTestResult?.success}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {migratingData ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="mr-2 h-4 w-4" />
                    )}
                    {migratingData ? 'Migrating Data...' : 'Start Migration'}
                  </Button>

                  {migrationResult && (
                    <div className={`p-4 rounded-lg ${migrationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {migrationResult.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        )}
                        <span className={`font-medium ${migrationResult.success ? 'text-green-700' : 'text-red-700'}`}>
                          {migrationResult.success ? 'Migration Successful!' : 'Migration Failed'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{migrationResult.message}</p>

                      {migrationResult.results && (
                        <div className="mt-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded p-2">
                              <p className="text-slate-500">Collections Created</p>
                              <p className="font-medium text-slate-800">{migrationResult.results.collections_created?.length || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-slate-500">Total Documents</p>
                              <p className="font-medium text-slate-800">
                                {Object.values(migrationResult.results.data_imported || {}).reduce((a, b) => a + b, 0)}
                              </p>
                            </div>
                          </div>
                          {migrationResult.results.errors?.length > 0 && (
                            <div className="mt-2 p-2 bg-red-50 rounded">
                              <p className="text-red-700 font-medium">Errors:</p>
                              <ul className="text-red-600 text-xs">
                                {migrationResult.results.errors.map((err, idx) => (
                                  <li key={idx}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrationDialogOpen(false)}>
              {migrationResult?.success ? 'Done' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Environment Variables Dialog */}
      <Dialog open={envVarsDialogOpen} onOpenChange={setEnvVarsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cable className="h-5 w-5 text-emerald-600" />
              Environment Variables
            </DialogTitle>
            <DialogDescription>
              View and edit environment variables. Changes require a service restart to take effect.
            </DialogDescription>
          </DialogHeader>

          {loadingEnvVars ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Tabs */}
              <div className="flex border-b mb-4">
                <button
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeEnvTab === 'backend'
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setActiveEnvTab('backend')}
                >
                  Backend (.env)
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeEnvTab === 'frontend'
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setActiveEnvTab('frontend')}
                >
                  Frontend (.env)
                </button>
              </div>

              {/* Variables List */}
              <div className="flex-1 overflow-auto pr-2 space-y-3">
                {Object.entries(editedEnvVars[activeEnvTab] || {}).map(([key, value]) => (
                  <div key={key} className="flex gap-3 items-start">
                    <div className="w-1/3 min-w-[200px]">
                      <Label className="text-xs text-slate-600 font-mono break-all">{key}</Label>
                    </div>
                    <div className="flex-1 flex gap-2">
                      <Input
                        value={value}
                        onChange={(e) => handleEnvVarChange(activeEnvTab, key, e.target.value)}
                        className="font-mono text-sm"
                        placeholder="(empty)"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(value);
                          toast.success("Copied to clipboard");
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {Object.keys(editedEnvVars[activeEnvTab] || {}).length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No environment variables found
                  </div>
                )}
              </div>

              {/* Save indicator */}
              {hasEnvChanges(activeEnvTab) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    You have unsaved changes in {activeEnvTab} environment
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4 border-t pt-4">
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={() => setEnvVarsDialogOpen(false)}>
                Close
              </Button>
              <Button
                onClick={() => handleSaveEnvVars(activeEnvTab)}
                disabled={savingEnvVars || !hasEnvChanges(activeEnvTab)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {savingEnvVars ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save {activeEnvTab === 'backend' ? 'Backend' : 'Frontend'} Variables
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default AdvancedToolsCard;
